import axios from 'axios';
import {
  CreateImportResult,
  ElementImportBundle,
  ProjectImportsBundle,
  ResourceCodeMapping,
  ResourceCodeMappingUpsertItem,
  ResourceCodeMappingsUpsertRequest,
} from '@shared/models/element-import.model';
import type {
  AiImportMappingRequest,
  AiImportMappingResponse,
} from '@shared/models/ai-import-mapping.model';

const AI_MAPPING_TIMEOUT_MS = 90_000;

/** HTTP layer for the CSV element-import feature (DEV-3298). */
export const elementImportsApi = {
  /** All imports for a project, with their rows and relevant mappings. */
  listByProject: (projectId: number): Promise<ProjectImportsBundle> =>
    axios
      .get<ProjectImportsBundle>('/element-imports', { params: { projectId } })
      .then((r) => r.data),

  /** One import plus its rows and mappings. */
  get: (id: string): Promise<ElementImportBundle> =>
    axios
      .get<ElementImportBundle>(`/element-imports/${id}`)
      .then((r) => r.data),

  /** Upload a kalkyl CSV; server parses and persists import + candidate rows. */
  upload: (projectId: number, file: File): Promise<CreateImportResult> => {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('projectId', String(projectId));
    return axios
      .post<CreateImportResult>('/element-imports', form)
      .then((r) => r.data);
  },

  remove: (id: string): Promise<void> =>
    axios.delete(`/element-imports/${id}`).then(() => undefined),

  /**
   * Upsert a batch of resource-code mappings in one transaction: each item's
   * reference, `discard` flag and (when a product is mapped) one unitFrom
   * entry. `projectId` set => project-scope rows; absent => org-scope.
   */
  upsertMappings: (
    mappings: ResourceCodeMappingUpsertItem[],
    projectId: number | undefined,
  ): Promise<ResourceCodeMapping[]> =>
    axios
      .put<ResourceCodeMapping[]>('/resource-code-mappings', {
        projectId,
        mappings,
      } satisfies ResourceCodeMappingsUpsertRequest)
      .then((r) => r.data),

  /**
   * Ask AI for a Boverket/CO2data.fi product (and optional factor) for one
   * import row. Does not apply the mapping.
   */
  suggestMapping: (
    importId: string,
    body: AiImportMappingRequest,
  ): Promise<AiImportMappingResponse> =>
    axios
      .post<AiImportMappingResponse>(
        `/element-imports/${importId}/ai-mapping-suggestions`,
        body,
        { timeout: AI_MAPPING_TIMEOUT_MS },
      )
      .then((r) => r.data),
};
